import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../../app/components/ui/dialog";
import { Button } from "../../../app/components/ui/button";
import { Input } from "../../../app/components/ui/input";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "../../../app/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../app/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "../../../app/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "../../../app/components/ui/command";
import { TRACKING_EVENTS } from "../../../constants/trackingEvents";
import { api } from "../../../lib/api";
import { applyLaravelValidationErrors, getErrorMessage, getLaravelValidationErrors } from "../../../lib/laravelErrors";
import { trackEvent } from "../../../lib/tracking";
import { useAuth } from "../../../providers/AuthProvider";
import type { Brand } from "../../products/hooks/useBrands";
import type { Category } from "../../products/hooks/useCategories";
import type { ExternalBarcodeData } from "./useBarcodeScanner";
import { cn } from "../../../app/components/ui/utils";

const DEALER_AUTO = "__auto__";

export type DealerOption = {
  id: string;
  name: string;
  is_default?: boolean;
  is_system_generated?: boolean;
};

type FormValues = {
  name: string;
  brand_name: string;
  category_name: string;
  purchase_price: string;
  selling_price: string;
  quantity: string;
};

function normalizeNameQuery(q: string): string {
  return q.trim().toLowerCase();
}

function NameSearchCombo(props: {
  label: string;
  description?: string;
  value: string;
  onChange: (v: string) => void;
  items: { id: string; name: string }[];
  placeholder: string;
  allowEmpty?: boolean;
  emptyLabel?: string;
}) {
  const { label, description, value, onChange, items, placeholder, allowEmpty, emptyLabel } = props;
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");

  const filtered = useMemo(() => {
    const nq = normalizeNameQuery(q);
    if (!nq) return items.slice(0, 50);
    return items.filter((it) => it.name.toLowerCase().includes(nq)).slice(0, 50);
  }, [items, q]);

  const exact = useMemo(() => {
    const t = q.trim();
    if (!t) return false;
    return items.some((it) => it.name.toLowerCase() === t.toLowerCase());
  }, [items, q]);

  return (
    <div className="space-y-2">
      <div>
        <div className="text-sm font-medium">{label}</div>
        {description ? <p className="text-xs text-muted-foreground mt-0.5">{description}</p> : null}
      </div>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className={cn("w-full justify-between font-normal", !value && "text-muted-foreground")}
          >
            <span className="truncate text-left">{value.trim() ? value : placeholder}</span>
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
          <Command shouldFilter={false}>
            <CommandInput placeholder="Search or type a new name…" value={q} onValueChange={setQ} />
            <CommandList>
              <CommandEmpty>
                {q.trim()
                  ? `No match — will use "${q.trim()}" as new ${label.toLowerCase()}`
                  : "Type to search or create"}
              </CommandEmpty>
              <CommandGroup>
                {allowEmpty ? (
                  <CommandItem
                    value="__empty__"
                    onSelect={() => {
                      onChange("");
                      setOpen(false);
                      setQ("");
                    }}
                  >
                    {emptyLabel ?? "None"}
                  </CommandItem>
                ) : null}
                {filtered.map((it) => (
                  <CommandItem
                    key={it.id}
                    value={it.name}
                    onSelect={() => {
                      onChange(it.name);
                      setOpen(false);
                      setQ("");
                    }}
                  >
                    {it.name}
                  </CommandItem>
                ))}
                {q.trim() && !exact ? (
                  <CommandItem
                    value={`__new__:${q.trim()}`}
                    onSelect={() => {
                      onChange(q.trim());
                      setOpen(false);
                      setQ("");
                    }}
                  >
                    Create &quot;{q.trim()}&quot;
                  </CommandItem>
                ) : null}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}

export function QuickAddProductModal(props: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  barcode: string;
  mode: "external" | "manual";
  externalPrefill: ExternalBarcodeData | null;
  brands: Brand[];
  categories: Category[];
  dealers: DealerOption[];
  onSuccess: (product: { id: string; name: string }) => void;
}) {
  const { open, onOpenChange, barcode, mode, externalPrefill, brands, categories, dealers, onSuccess } = props;
  const { token } = useAuth();
  const [rootError, setRootError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [dealerSelect, setDealerSelect] = useState<string>(DEALER_AUTO);

  const defaultValues = useMemo<FormValues>(() => {
    return {
      name: mode === "external" && externalPrefill ? externalPrefill.name : "",
      brand_name: mode === "external" && externalPrefill?.brand ? externalPrefill.brand : "",
      category_name: mode === "external" && externalPrefill?.category ? externalPrefill.category : "",
      purchase_price: "0",
      selling_price: "",
      quantity: "1",
    };
  }, [mode, externalPrefill]);

  const form = useForm<FormValues>({ defaultValues });

  useEffect(() => {
    if (!open) return;
    form.reset(defaultValues);
    setRootError(null);
    const def = dealers.find((d) => d.is_default || d.is_system_generated);
    setDealerSelect(def ? String(def.id) : DEALER_AUTO);
  }, [open, defaultValues, form, dealers]);

  const brandItems = useMemo(() => brands.map((b) => ({ id: b.id, name: b.name })), [brands]);
  const categoryItems = useMemo(() => categories.map((c) => ({ id: c.id, name: c.name })), [categories]);
  const imageUrl = mode === "external" && externalPrefill?.image ? externalPrefill.image : null;

  async function onSubmit(values: FormValues) {
    if (!token) return;
    setRootError(null);
    setSubmitting(true);
    try {
      const qty = Number.parseFloat(values.quantity);
      const sell = Number.parseFloat(values.selling_price);
      const purchase = Number.parseFloat(values.purchase_price.trim());

      const res = await api.products.quickCreate({
        token,
        body: {
          name: values.name.trim(),
          barcode: barcode.trim(),
          brand_name: values.brand_name.trim() || null,
          category_name: values.category_name.trim() || null,
          purchase_price: purchase,
          selling_price: sell,
          quantity: qty,
          dealer_id: dealerSelect === DEALER_AUTO ? null : Number(dealerSelect),
          barcode_source: mode === "external" ? "external" : "manual",
        },
      });
      trackEvent(TRACKING_EVENTS.PRODUCT_QUICK_CREATED);
      trackEvent(TRACKING_EVENTS.BARCODE_AUTO_PRODUCT_CREATED);
      onSuccess({ id: String(res.product.id), name: res.product.name });
      onOpenChange(false);
    } catch (e) {
      const errors = getLaravelValidationErrors(e);
      if (errors) {
        applyLaravelValidationErrors(form.setError, errors);
        setRootError("Please fix the highlighted fields.");
        return;
      }
      setRootError(getErrorMessage(e, "Failed to create product"));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Quick add product</DialogTitle>
          <DialogDescription>
            {mode === "external"
              ? "We found product details online. Add pricing and stock, then save to sell immediately."
              : "No product matched this barcode. Add details to create stock and sell in one step."}
          </DialogDescription>
        </DialogHeader>

        {rootError ? <p className="text-sm text-destructive">{rootError}</p> : null}

        {imageUrl ? (
          <div className="flex justify-center">
            <img src={imageUrl} alt="" className="max-h-32 rounded-md border object-contain bg-muted" />
          </div>
        ) : null}

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              rules={{ required: "Name is required" }}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name</FormLabel>
                  <FormControl>
                    <Input placeholder="Product name" autoFocus={open} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="space-y-2">
              <div className="text-sm font-medium">Barcode</div>
              <Input value={barcode} readOnly className="bg-muted/50" />
            </div>

            <FormField
              control={form.control}
              name="brand_name"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <NameSearchCombo
                      label="Brand"
                      description="Search an existing brand or create a new one."
                      value={field.value}
                      onChange={field.onChange}
                      items={brandItems}
                      placeholder="No brand"
                      allowEmpty
                      emptyLabel="No brand"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="category_name"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <NameSearchCombo
                      label="Category"
                      description="Leave empty to use Uncategorized."
                      value={field.value}
                      onChange={field.onChange}
                      items={categoryItems}
                      placeholder="Uncategorized"
                      allowEmpty
                      emptyLabel="Uncategorized"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-2 gap-3">
              <FormField
                control={form.control}
                name="purchase_price"
                rules={{
                  required: "Purchase price is required",
                  validate: (v) => {
                    const n = Number.parseFloat(String(v).trim());
                    if (!Number.isFinite(n) || n < 0) return "Enter a valid purchase price (0 or more)";
                    return true;
                  },
                }}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Purchase price</FormLabel>
                    <FormControl>
                      <Input type="number" min={0} step="0.01" placeholder="0" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="selling_price"
                rules={{ required: "Selling price is required" }}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Selling price</FormLabel>
                    <FormControl>
                      <Input type="number" min={0} step="0.01" placeholder="0" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="quantity"
              rules={{
                required: "Quantity is required",
                validate: (v) => {
                  const n = Number.parseFloat(v);
                  if (!Number.isFinite(n) || n <= 0) return "Quantity must be greater than 0";
                  return true;
                },
              }}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Quantity</FormLabel>
                  <FormControl>
                    <Input type="number" min={0.001} step="any" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="space-y-2">
              <div className="text-sm font-medium">Dealer</div>
              <Select value={dealerSelect} onValueChange={setDealerSelect}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Supplier" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={DEALER_AUTO}>Default supplier (auto)</SelectItem>
                  {dealers.map((d) => (
                    <SelectItem key={d.id} value={String(d.id)}>
                      {d.name}
                      {d.is_system_generated ? " (system)" : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                The default walk-in supplier is created automatically when needed.
              </p>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={submitting}>
                {submitting ? "Saving…" : "Save & add to cart"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
